import { formatNumber } from '@gonfalon/format/formatNumber';
import type { TooltipFormatterCallback, TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { escape as escapeHTMLText } from 'lodash';

import type { Granularity, UsageTooltipComponent } from '../../../../types';
import { pluralizeAbbreviatedName } from '../../../../utils';
import { formatUsageTooltipDate } from '../formatUsageTooltipDate';

import styles from './UsageDetailChart.module.css';

export type { UsageTooltipComponent } from '../../../../types';

type TooltipFormatterProps = {
  abbreviatedName: string;
  timestamp: number;
  amount: number;
  primaryContext?: string;
  condenseLabels?: boolean;
  precision: number;
  granularity: Granularity;
  // When true, the value is a projection rather than actual usage.
  isProjected?: boolean;
};

type ComponentTooltipFormatterProps = {
  abbreviatedName: string;
  timestamp: number;
  amount: number;
  precision: number;
  granularity: Granularity;
  components: readonly UsageTooltipComponent[];
};

function formatTooltipComponentValue(
  component: UsageTooltipComponent,
  timestamp: number,
  fallbackPrecision: number,
): string {
  if (!component.isIncluded) {
    return 'Not included';
  }

  const value = component.series.find((point) => point.time === timestamp)?.value;
  return value === null || value === undefined
    ? '—'
    : formatNumber(value, { maximumFractionDigits: component.precision ?? fallbackPrecision });
}

export function formatUsageComponentTooltipHTML({
  abbreviatedName,
  timestamp,
  amount,
  precision,
  granularity,
  components,
}: ComponentTooltipFormatterProps): string {
  const rows = components.map(
    (component) => `
      <div class="${styles.tooltipRow}">
        <span>${escapeHTMLText(component.label)}</span>
        <span class="${styles.tooltipRowValue}">${formatTooltipComponentValue(component, timestamp, precision)}</span>
      </div>
    `,
  );
  const unit = escapeHTMLText(pluralizeAbbreviatedName(abbreviatedName, amount));

  return `
    <div class="${styles.tooltipContainer}">
      <div class="${styles.tooltipValue}">
        ${formatNumber(amount, { maximumFractionDigits: precision })} ${unit}
      </div>
      <div class="${styles.tooltipSubText}">
        ${formatUsageTooltipDate(timestamp, granularity, { separateTime: true })}
      </div>
      ${rows.length > 0 ? `<div class="${styles.tooltipRows}">${rows.join('')}</div>` : ''}
    </div>
  `;
}

/**
 * Formats HTML for the usage tooltip.
 */
export function formatUsageTooltipHTML({
  abbreviatedName,
  timestamp,
  amount,
  primaryContext,
  precision,
  condenseLabels = false,
  granularity,
  isProjected = false,
}: TooltipFormatterProps): string {
  const formattedAmount = formatNumber(amount, { maximumFractionDigits: precision });
  const abbreviated = escapeHTMLText(pluralizeAbbreviatedName(abbreviatedName, amount));
  const formattedDate = formatUsageTooltipDate(timestamp, granularity);
  const context = primaryContext ? ` (${escapeHTMLText(primaryContext)})` : '';
  const amountSuffix = condenseLabels ? '' : ` ${abbreviated}${context}`;
  const projectedSuffix = isProjected ? ' (projected)' : '';

  return `
    <div class="${styles.tooltipContainer}">
      <div class="${styles.tooltipValue}">
        ${formattedAmount}${amountSuffix}${projectedSuffix}
      </div>
      <div class="${styles.tooltipSubText}">
        ${formattedDate}
      </div>
    </div>
  `;
}

export function getTooltipFormatter(
  abbreviatedName: string,
  precision: number,
  granularity: Granularity,
  condenseLabels?: boolean,
  components?: readonly UsageTooltipComponent[],
): TooltipFormatterCallback<TopLevelFormatterParams> {
  return function (params) {
    const items = Array.isArray(params) ? params : [params];
    type PointData = { value: [number, number | null]; primaryContext?: string };

    // Only a non-"Projected" series counts as actual. Don't fall back to items[0]: on the dashed
    // segment ECharts may pass only "Projected", and treating that as actual would drop the
    // "(projected)" suffix and mislabel a projection as real usage.
    const actual = items.find((item) => item.seriesName !== 'Projected');
    const actualData = actual?.data as PointData | undefined;
    const actualAmount = actualData?.value[1];

    if (actualData && actualAmount !== null && actualAmount !== undefined) {
      if (components) {
        return formatUsageComponentTooltipHTML({
          abbreviatedName,
          timestamp: actualData.value[0],
          amount: actualAmount,
          precision,
          granularity,
          components,
        });
      }
      return formatUsageTooltipHTML({
        abbreviatedName,
        timestamp: actualData.value[0],
        amount: actualAmount,
        primaryContext: actualData.primaryContext,
        precision,
        condenseLabels,
        granularity,
      });
    }

    // Otherwise fall back to the projected series so the dashed segment is interactive.
    const projected = items.find((item) => item.seriesName === 'Projected');
    const projectedData = projected?.data as PointData | undefined;
    const projectedAmount = projectedData?.value[1];

    if (projectedData && projectedAmount !== null && projectedAmount !== undefined) {
      return formatUsageTooltipHTML({
        abbreviatedName,
        timestamp: projectedData.value[0],
        amount: projectedAmount,
        precision,
        condenseLabels,
        granularity,
        isProjected: true,
      });
    }

    // If neither series has a value at this point, hide the tooltip.
    return '';
  };
}
